import {
  useCallback,
  useId,
  useState,
  useLayoutEffect,
  useRef,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

export type RequiredIssue = {
  key: string;
  message: string;
};

type RequiredFeedbackState = {
  messages: Record<string, string>;
  attempt: number;
};

const EMPTY_STATE: RequiredFeedbackState = {
  messages: {},
  attempt: 0,
};

function focusRequiredField(key: string) {
  if (typeof document === 'undefined') return;

  // Two frames lets a wizard open a collapsed section before we look for the
  // field. A short timeout is the fallback for background tabs where rAF can
  // be throttled.
  let finished = false;
  const focus = () => {
    if (finished) return;
    finished = true;
    const wrapper = Array.from(
      document.querySelectorAll<HTMLElement>('[data-required-field]'),
    ).find((node) => node.dataset.requiredField === key);
    if (!wrapper) return;

    wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const target = wrapper.querySelector<HTMLElement>(
      '[data-validation-focus], input:not([disabled]), textarea:not([disabled]), '
      + 'select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    (target ?? wrapper).focus({ preventScroll: true });
  };

  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => requestAnimationFrame(focus));
  }
  setTimeout(focus, 80);
}

export function normalizeRequiredIssues(
  issues: Array<RequiredIssue | null | false | undefined>,
): RequiredIssue[] {
  const normalized = new Map<string, string>();
  for (const issue of issues) {
    if (!issue || !issue.key || !issue.message) continue;
    if (!normalized.has(issue.key)) normalized.set(issue.key, issue.message);
  }
  return Array.from(normalized, ([key, message]) => ({ key, message }));
}

export function useRequiredFeedback() {
  const [state, setState] = useState<RequiredFeedbackState>(EMPTY_STATE);

  const report = useCallback((
    rawIssues: Array<RequiredIssue | null | false | undefined>,
    options: { focus?: boolean } = {},
  ) => {
    const issues = normalizeRequiredIssues(rawIssues);
    if (issues.length === 0) {
      setState((current) => (
        Object.keys(current.messages).length === 0 ? current : EMPTY_STATE
      ));
      return true;
    }

    setState((current) => ({
      messages: Object.fromEntries(issues.map(({ key, message }) => [key, message])),
      attempt: current.attempt + 1,
    }));
    const firstIssue = issues[0];
    if (options.focus !== false && firstIssue) focusRequiredField(firstIssue.key);
    return false;
  }, []);

  const clear = useCallback((key: string) => {
    setState((current) => {
      if (!current.messages[key]) return current;
      const messages = { ...current.messages };
      delete messages[key];
      return { ...current, messages };
    });
  }, []);

  const clearAll = useCallback(() => setState(EMPTY_STATE), []);

  return {
    messages: state.messages,
    attempt: state.attempt,
    report,
    clear,
    clearAll,
  };
}

export function RequiredField({
  fieldKey,
  message,
  attempt,
  surface = false,
  className = '',
  children,
  ...props
}: {
  fieldKey: string;
  message?: string | null;
  attempt: number;
  surface?: boolean;
  children?: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, 'children'>) {
  const rawId = useId();
  const messageId = `required-${rawId.replaceAll(':', '')}`;
  const invalid = Boolean(message);
  const fieldRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root=fieldRef.current;
    if(!root)return;
    const controls=Array.from(root.querySelectorAll<HTMLElement>('input,select,textarea,button.dd-trigger')).filter(control=>control.closest('[data-required-field]')===root);
    const label=root.querySelector<HTMLLabelElement>('label');
    if(controls.length===1){
      const control=controls[0]!;
      if(label && !label.htmlFor && !label.contains(control)) {
        control.id ||= `${messageId}-control`;
        label.id ||= `${messageId}-label`;
        label.htmlFor=control.id;
        if(control.tagName==='BUTTON'&&!control.hasAttribute('aria-label'))control.setAttribute('aria-labelledby',label.id);
      }
      if(invalid){control.setAttribute('aria-invalid','true');control.setAttribute('aria-describedby',messageId);}
      else if(control.getAttribute('aria-describedby')===messageId){control.removeAttribute('aria-invalid');control.removeAttribute('aria-describedby');}
    }
  });

  return (
    <div
      {...props}
      ref={fieldRef}
      data-required-field={fieldKey}
      className={[
        'validation-field',
        surface ? 'validation-surface' : '',
        invalid ? 'is-invalid' : '',
        invalid ? `validation-bump-${attempt % 2}` : '',
        className,
      ].filter(Boolean).join(' ')}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? messageId : undefined}
      tabIndex={-1}
    >
      {children}
      {invalid && (
        <div
          key={attempt}
          id={messageId}
          className="validation-message"
          role="alert"
        >
          <span aria-hidden="true">!</span>
          {message}
        </div>
      )}
    </div>
  );
}
